import { Box } from "@mui/material";

/**
 * A customizable Box component built on top of Material-UI's Box component.
 * This component provides default styling for padding, margin, borderRadius, boxShadow, and more,
 * while allowing for extensive customization through props.
 *
 * @component
 * @param {Object} props - The props for the component.
 * @param {number|string} [props.padding=3] - The padding applied to the Box. Can be a number (for theme spacing) or a string (e.g., "16px"). Defaults to 3.
 * @param {number|string} [props.margin=3] - The margin applied to the Box. Can be a number (for theme spacing) or a string (e.g., "16px"). Defaults to 3.
 * @param {number|string} [props.borderRadius=1.5] - The border radius applied to the Box. Can be a number (for theme spacing) or a string (e.g., "8px"). Defaults to 1.5.
 * @param {string} [props.boxShadow="0px 1px 2px 0px rgba(0,0,0,.2)"] - The box shadow applied to the Box. Defaults to a subtle shadow.
 * @param {string} [props.height="100%"] - The height of the Box. Defaults to "100%".
 * @param {string} [props.overflow="hidden"] - The overflow behavior of the Box. Defaults to "hidden".
 * @param {string} [props.backgroundColor="common.white"] - The background color of the Box. Defaults to "common.white".
 * @param {string} [props.testid] - A `data-testid` attribute for testing purposes. Optional.
 * @param {string} [props.className] - Additional CSS class names to apply to the Box. Optional.
 * @param {string} [props.maxWidth] - The maximum width of the Box. Optional.
 * @param {Object} [props.styles] - Inline styles to apply to the Box. Optional.
 * @param {Object|Function} [props.sx] - Additional sx props to apply to the Box. Can be an object or a function that receives the theme. Optional.
 * @param {ReactNode} props.children - The content to be rendered inside the Box.
 * @returns {JSX.Element} The rendered Box component.
 *
 * @example
 * // Example usage of MiBox
 * <MiBox
 *   padding={4}
 *   margin={2}
 *   borderRadius={2}
 *   boxShadow="0px 2px 4px rgba(0,0,0,0.1)"
 *   backgroundColor="grey.100"
 *   testid="custom-box"
 *   maxWidth="500px"
 * >
 *   <Typography variant="h6">This is a custom box!</Typography>
 * </MiBox>
 */
export const MiBox = ({
  padding = 3,
  margin = 3,
  borderRadius = 1.5,
  boxShadow = "0px 1px 2px 0px rgba(0,0,0,.2)",
  height = "100%",
  overflow = "auto",
  backgroundColor = "common.white",
  testid,
  "data-testid": dataTestId,
  className,
  maxWidth,
  styles,
  sx,
  children,
}) => (
  <Box
    className={className}
    padding={padding}
    margin={margin}
    borderRadius={borderRadius}
    overflow={overflow}
    backgroundColor={backgroundColor}
    data-testid={testid || dataTestId}
    styles={styles}
    sx={(theme) => ({
      boxShadow,
      height,
      maxWidth,
      [theme.breakpoints.down("sm")]: {
        maxWidth: "100%",
        padding: theme.spacing(2),
      },
      ...(typeof sx === "function" ? sx(theme) : sx),
    })}
  >
    {children}
  </Box>
);
